import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NewProviderInformation } from '@core/model';
import { OrderingProviderPacimService } from '@core/service';
import { PortalProducts } from '@shared/enumeration';
import { isNull } from '@shared/utility';
import {
  PhysicianAccountDTO,
  PhysicianAccountPgpsDTO
} from '@mynexus/mynexus-ui-lib';
import { debounce } from 'lodash-es';
import { finalize } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { OrderingProviderExistingData, OrderingProviderPac } from '../../model';
import { NewProviderComponent } from '../new-provider/new-provider.component';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'ordering-provider-tab-pac',
    templateUrl: './ordering-provider-tab-pac.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OrderingProviderTabPacComponent implements OnInit, OnDestroy {
  @Input() memberId!: string;
  @Input() orderingProvider: OrderingProviderPac | null;
  @Input() product: PortalProducts.PACIM | PortalProducts.PacCommercial;
  @Input() tenantId!: string;
  @Input() facilityType!: string;
  @Input() isGoldCardVisible = false;
  @Input() isGoldCardProvider = false;
  @Input() goldCardAuthorizationType!: string;

  @Output() backStep = new EventEmitter<void>();
  @Output() cancelProcess = new EventEmitter<void>();
  @Output() updateProvider = new EventEmitter<OrderingProviderPac>();
  @Output() submitStep = new EventEmitter<OrderingProviderPac>();

  @ViewChild(NewProviderComponent) newProviderComponent: NewProviderComponent;

  newProviderDefaultData?: NewProviderInformation;
  selectedProvider?: PhysicianAccountDTO;
  searching = false;
  searchResult?: PhysicianAccountPgpsDTO[];
  submitData: OrderingProviderPac = null;

  private autoUpdateDebounced = debounce(() => {
    this.updateProvider.emit(this.submitData);
  }, 500);

  private readonly subs = new SubSink();

  constructor(
    private ref: ChangeDetectorRef,
    private orderingProviderService: OrderingProviderPacimService
  ) { }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    if (this.orderingProvider) {
      this.setStateData();

      this.ref.markForCheck();
    }
  }

  onCancel(): void {
    this.cancelProcess.emit();
  }

  onClickBack(): void {
    this.backStep.emit();
  }

  onSubmit(): void {
    if (this.submitData.physicianContact) {
      this.updateProvider.emit(this.submitData);
    }

    this.submitStep.emit(this.submitData);
  }

  onSearch(npi: string): void {
    this.newProviderDefaultData = null;

    if (this.newProviderComponent) {
      this.newProviderComponent.reset();
    }

    this.searchResult = null;
    this.selectedProvider = null;
    this.isGoldCardProvider = false;
    this.submitData = null;
    this.searching = true;
    this.goldCardAuthorizationType = null;

    this.subs.sink = this.orderingProviderService.findOrderingProvider(
      this.tenantId,
      this.memberId,
      npi,
      this.facilityType ?? null
    ).pipe(
      finalize(() => {
        this.searching = false;
        this.ref.markForCheck();
      })
    ).subscribe(result => {
      this.searchResult = result;

      if (result.length === 1) {
        this.submitData = this.parseProviderData(result[0]);
        this.selectedProvider = this.submitData.provider.physicianAccountViewDTO;
        this.isGoldCardProvider = this.submitData.provider.isGoldCardProvider;
        this.goldCardAuthorizationType = this.submitData.provider.goldCardAuthorizationType;
        this.updateProvider.emit(this.submitData);
      }
    });
  }

  selectProvider(provider: PhysicianAccountPgpsDTO): void {
    this.submitData = this.parseProviderData(provider);
    this.selectedProvider = this.submitData.provider.physicianAccountViewDTO;
    this.isGoldCardProvider = this.submitData.provider.isGoldCardProvider;
    this.goldCardAuthorizationType = this.submitData.provider.goldCardAuthorizationType;
    this.searchResult = null;

    this.updateProvider.emit(this.submitData);
  }

  setNewProvider(physicianContact: NewProviderInformation | null): void {
    this.submitData = isNull(physicianContact) ? null : { physicianContact };

    if (this.submitData) {
      this.autoUpdateDebounced();
    }
  }

  private parseProviderData(
    data: PhysicianAccountPgpsDTO
  ): OrderingProviderExistingData {
    const { physicianPgpDTOList: pgpList, physicianAccountDTO } = data;
    const { physicianAccountId, physicianPgpId, isGoldCardProvider, goldCardAuthorizationType } = pgpList.find(pgp => pgp.isGoldCardProvider === true) ?? pgpList[0];

    return {
      provider: {
        physicianAccountViewDTO: physicianAccountDTO,
        orderingPhysicianAccountId: physicianAccountId,
        orderingPhysicianPgpId: physicianPgpId,
        isGoldCardProvider: isGoldCardProvider,
        goldCardAuthorizationType: goldCardAuthorizationType
      }
    };
  }

  private setStateData(): void {
    this.submitData = this.orderingProvider;

    if (this.orderingProvider.provider) {
      this.selectedProvider = this.submitData.provider.physicianAccountViewDTO;
      this.isGoldCardProvider = this.submitData.provider.isGoldCardProvider;
      this.goldCardAuthorizationType = this.submitData.provider.goldCardAuthorizationType;
    } else {
      this.newProviderDefaultData = this.submitData.physicianContact;
    }
  }
}
